// Push Notification Service
import api from './api';

class PushNotificationService {
    constructor() {
        this.isSupported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
        this.permission = this.isSupported ? Notification.permission : 'denied';
        this.serviceWorkerRegistration = null;
        this.subscription = null;
        this.vapidPublicKey = 'BOzb0rG6XoHHflmqjWcpIpvZy19GTUN20vgjIBBsOGkUh4Jt1jhG2C0VWTzw1AcM0HJE_ccWdNhcnzWe32SEcFU';
    }

    // Request notification permission
    async requestPermission() {
        if (!this.isSupported) {
            throw new Error('Push notifications are not supported in this browser');
        }

        if (this.permission === 'granted') {
            return true;
        }

        if (this.permission === 'denied') {
            throw new Error('Notification permission has been denied');
        }

        const permission = await Notification.requestPermission();
        this.permission = permission;
        return permission === 'granted';
    }

    // Register service worker
    async registerServiceWorker() {
        if (!('serviceWorker' in navigator)) {
            throw new Error('Service workers are not supported');
        }

        try {
            const registration = await navigator.serviceWorker.register('/sw.js');
            this.serviceWorkerRegistration = registration;
            
            // Wait for service worker to be ready
            await navigator.serviceWorker.ready;
            
            return registration;
        } catch (error) {
            console.error('Service worker registration failed:', error);
            throw error;
        }
    }

    // Subscribe to push notifications
    async subscribeToPush() {
        if (!this.serviceWorkerRegistration) {
            await this.registerServiceWorker();
        }

        try {
            // Always clear existing subscription first to avoid VAPID key conflicts
            const existingSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
            if (existingSubscription) {
                console.log('Clearing existing subscription to avoid VAPID key conflicts...');
                await existingSubscription.unsubscribe();
            }
            
            // Now subscribe with the current VAPID key
            const subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
            });

            this.subscription = subscription;
            return subscription;
        } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
            
            // If it's still a VAPID key error, try one more time with a clean slate
            if (error.name === 'InvalidStateError' && error.message.includes('applicationServerKey')) {
                console.log('Attempting to force clear all subscriptions and retry...');
                try {
                    // Force clear all subscriptions
                    await this.clearAllSubscriptions();
                    
                    // Wait a bit for cleanup to complete
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Try subscribing again
                    const subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
                    });
                    
                    this.subscription = subscription;
                    console.log('Successfully subscribed after force cleanup');
                    return subscription;
                } catch (retryError) {
                    console.error('Failed to subscribe even after force cleanup:', retryError);
                    throw retryError;
                }
            }
            
            throw error;
        }
    }

    // Save subscription to server
    async saveSubscription(subscription) {
        try {
            // Check if user is authenticated
            const authUser = localStorage.getItem('auth_user');
            if (!authUser) {
                console.log('User not authenticated, skipping push subscription save');
                return false;
            }
            
            // Additional check - verify the auth token is valid
            try {
                const parsedAuth = JSON.parse(authUser);
                if (!parsedAuth.token) {
                    console.log('No auth token found, skipping push subscription save');
                    return false;
                }
            } catch (e) {
                console.log('Invalid auth user data, skipping push subscription save');
                return false;
            }

            console.log('Saving push subscription:', subscription);
            const response = await api.post('/push-subscriptions', subscription);
            console.log('Push subscription API response:', response);
            
            if (!response) {
                console.error('No response received from API');
                return false;
            }
            
            return response.success;
        } catch (error) {
            console.error('Failed to save push subscription:', error);
            return false;
        }
    }

    // Remove subscription from server
    async removeSubscription() {
        try {
            // Check if user is authenticated
            const authUser = localStorage.getItem('auth_user');
            if (!authUser) {
                console.log('User not authenticated, skipping push subscription removal');
                return false;
            }
            
            // Additional check - verify the auth token is valid
            try {
                const parsedAuth = JSON.parse(authUser);
                if (!parsedAuth.token) {
                    console.log('No auth token found, skipping push subscription removal');
                    return false;
                }
            } catch (e) {
                console.log('Invalid auth user data, skipping push subscription removal');
                return false;
            }

            if (this.subscription) {
                const response = await api.delete('/push-subscriptions', {
                    data: { endpoint: this.subscription.endpoint }
                });
                return response.success;
            }
            return true;
        } catch (error) {
            console.error('Failed to remove push subscription:', error);
            return false;
        }
    }

    // Send push notification (for testing)
    async sendNotification(title, options = {}) {
        if (this.permission !== 'granted') {
            throw new Error('Notification permission not granted');
        }

        const notification = new Notification(title, {
            icon: '/storage/logo-192.png',
            badge: '/storage/logo-192.png',
            ...options
        });

        return notification;
    }

    // Show in-app notification
    showInAppNotification(notification) {
        // Create notification element
        const notificationElement = document.createElement('div');
        notificationElement.className = 'fixed top-4 right-4 bg-white shadow-lg rounded-lg p-4 max-w-sm z-50 border-l-4 border-blue-500 animate-slide-in';
        notificationElement.innerHTML = `
            <div class="flex items-start">
                <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" />
                        </svg>
                    </div>
                </div>
                <div class="ml-3 flex-1">
                    <h4 class="text-sm font-medium text-gray-900">${notification.title}</h4>
                    <p class="text-sm text-gray-500 mt-1">${notification.body || notification.message}</p>
                </div>
                <button onclick="this.parentElement.parentElement.remove()" class="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>
        `;

        // Add CSS animation
        if (!document.getElementById('notification-styles')) {
            const style = document.createElement('style');
            style.id = 'notification-styles';
            style.textContent = `
                @keyframes slide-in {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slide-in {
                    animation: slide-in 0.3s ease-out;
                }
            `;
            document.head.appendChild(style);
        }

        // Add to page
        document.body.appendChild(notificationElement);

        // Auto remove after 5 seconds
        setTimeout(() => {
            if (notificationElement.parentNode) {
                notificationElement.style.animation = 'slide-in 0.3s ease-out reverse';
                setTimeout(() => {
                    if (notificationElement.parentNode) {
                        notificationElement.remove();
                    }
                }, 300);
            }
        }, 5000);
    }

    // Convert VAPID key
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    // Clear all existing subscriptions
    async clearAllSubscriptions() {
        try {
            // Clear from current service worker registration
            if (this.serviceWorkerRegistration) {
                const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
                if (subscription) {
                    await subscription.unsubscribe();
                    console.log('Cleared existing push subscription from current registration');
                }
            }
            
            // Also try to clear from any active service worker
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                try {
                    const subscription = await registration.pushManager.getSubscription();
                    if (subscription) {
                        await subscription.unsubscribe();
                        console.log('Cleared existing push subscription from registration');
                    }
                } catch (error) {
                    console.warn('Failed to clear subscription from registration:', error);
                }
            }
            
            // Clear local state
            this.subscription = null;
            
            console.log('All subscriptions cleared');
        } catch (error) {
            console.warn('Failed to clear existing subscriptions:', error);
        }
    }

    // Initialize push notifications
    async initialize() {
        try {
            if (!this.isSupported) {
                console.warn('Push notifications not supported in this browser');
                return false;
            }

            // Check if user is authenticated
            const authUser = localStorage.getItem('auth_user');
            if (!authUser) {
                console.log('User not authenticated, cannot initialize push notifications');
                return false;
            }
            
            // Additional check - verify the auth token is valid
            try {
                const parsedAuth = JSON.parse(authUser);
                if (!parsedAuth.token) {
                    console.log('No auth token found, cannot initialize push notifications');
                    return false;
                }
            } catch (e) {
                console.log('Invalid auth user data, cannot initialize push notifications');
                return false;
            }

            // Request permission
            const permissionGranted = await this.requestPermission();
            if (!permissionGranted) {
                return false;
            }

            // Register service worker
            await this.registerServiceWorker();

            // Subscribe to push notifications (this will handle VAPID key changes)
            const subscription = await this.subscribeToPush();
            
            // Save subscription to server
            await this.saveSubscription(subscription);

            console.log('Push notifications initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize push notifications:', error);
            
            // If it's a VAPID key error, try to clear and reinitialize
            if (error.name === 'InvalidStateError' && error.message.includes('applicationServerKey')) {
                console.log('Attempting to clear existing subscription and retry...');
                try {
                    await this.clearAllSubscriptions();
                    const subscription = await this.subscribeToPush();
                    await this.saveSubscription(subscription);
                    console.log('Push notifications reinitialized successfully after clearing old subscription');
                    return true;
                } catch (retryError) {
                    console.error('Failed to reinitialize after clearing subscription:', retryError);
                }
            }
            
            return false;
        }
    }

    // Check if push notifications are supported and enabled
    isEnabled() {
        return this.isSupported && this.permission === 'granted' && this.subscription !== null;
    }

    // Get current subscription
    getSubscription() {
        return this.subscription;
    }

    // Test push notification
    async testNotification() {
        if (!this.isEnabled()) {
            throw new Error('Push notifications not enabled');
        }

        try {
            // Send test notification to server
            const response = await api.post('/notifications', {
                type: 'push',
                title: 'Test Notification',
                message: 'This is a test push notification from Spoon & Spice!',
                data: {
                    type: 'test',
                    timestamp: new Date().toISOString()
                }
            });

            return response.data.success;
        } catch (error) {
            console.error('Failed to send test notification:', error);
            return false;
        }
    }

    // Force clear all subscriptions and reinitialize (for troubleshooting)
    async forceReinitialize() {
        try {
            console.log('Force reinitializing push notifications...');
            
            // Clear all existing subscriptions
            await this.clearAllSubscriptions();
            
            // Clear local state
            this.subscription = null;
            this.serviceWorkerRegistration = null;
            
            // Reinitialize
            return await this.initialize();
        } catch (error) {
            console.error('Failed to force reinitialize push notifications:', error);
            return false;
        }
    }

    // Complete reset of push notification state (for debugging)
    async completeReset() {
        try {
            console.log('Performing complete push notification reset...');
            
            // Clear all subscriptions
            await this.clearAllSubscriptions();
            
            // Unregister all service workers
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
                console.log('Unregistered service worker');
            }
            
            // Clear all caches
            const caches = await caches.keys();
            for (const cacheName of caches) {
                await caches.delete(cacheName);
                console.log(`Cleared cache: ${cacheName}`);
            }
            
            // Reset local state
            this.subscription = null;
            this.serviceWorkerRegistration = null;
            this.permission = 'default';
            
            console.log('Complete reset finished. You can now reinitialize push notifications.');
            return true;
        } catch (error) {
            console.error('Failed to complete reset:', error);
            return false;
        }
    }
}

export default new PushNotificationService();
